import { GlobalTheme } from "../types/GlobalTheme";
import { dark, light } from "@gds-react/themes";

export function getGlobalTheme(appearance: "dark" | "light") {
  const theme = appearance === "dark" ? dark : light;

  return {
    tokens: theme,
    colors: {
      background: {
        layers: {
          900: "#000",
          800: theme["color.background.surface.paper.00"],
          700: theme["color.background.surface.paper.01"],
          600: theme["color.background.surface.paper.02"],
          500: theme["color.background.neutral.subtle.hovered"],
          400: theme["color.background.neutral.strong.selected"],
          300: theme["color.background.surface.accent.strong.default"],
        },
        grundfos: {
          primary: "rgb(17, 73, 123)",
        },
        test: {
          primary: "green",
        },
      },
      border: {
        inverse: theme["color.border.inverse"],
        weak: theme["color.border.neutral.subtle.default"],
        hovered: theme["color.border.neutral.strong.hovered"],
        strong: theme["color.border.neutral.strong.default"],
      },
      icon: {
        brand: theme["color.icon.attention.strongest.default"],
        disabled: theme["color.icon.neutral.disabled"],
        warning: theme["color.icon.support.warning.strongest.default"],
        error: theme["color.icon.support.danger.subtle.default"],
        success: theme["color.icon.support.success.strongest.default"],
        secondary: theme["color.icon.neutral.secondary"],
        tertiary: theme["color.icon.neutral.tertiary"],
      },
      text: {
        primary: { color: theme["color.text.neutral.primary"] },
        secondary: { color: theme["color.text.neutral.secondary"] },
        tertiary: { color: theme["color.text.neutral.tertiary"] },
        warning: {
          color: theme["color.icon.support.warning.strongest.default"],
        },
        error: { color: theme["color.text.support.danger.subtle.default"] },
      },
      input: {
        primary: {
          background: theme["color.background.neutral.strong.default"],
          border: theme["color.border.neutral.strongest.default"],
          borderFocus: theme["color.border.focused"],
          borderError: theme["color.border.support.danger.strongest.default"],
          borderDisabled: theme["color.border.neutral.disabled"],
          text: theme["color.text.neutral.primary"],
          textDisabled: theme["color.text.neutral.disabled"],
        },
      },
      select: {
        primary: {
          background: theme["color.background.neutral.strong.default"],
          backgroundHover: theme["color.background.neutral.strong.hovered"],
          border: theme["color.border.neutral.strongest.default"],
          borderFocus: theme["color.border.focused"],
          borderError: theme["color.border.support.danger.strongest.default"],
          borderDisabled: theme["color.border.neutral.disabled"],
          text: theme["color.text.neutral.primary"],
          textDisabled: theme["color.text.neutral.disabled"],
        },
      },
      switch: {
        primary: {
          background: theme["color.background.neutral.strong.default"],
          backgroundHover: theme["color.background.neutral.strong.hovered"],
          backgroundHandler: "#121516", // Magic number
          backgroundSelected: theme["color.background.neutral.strong.pressed"],
          border: theme["color.border.neutral.strongest.default"],
          borderError: theme["color.border.support.danger.strongest.default"],
          text: theme["color.text.neutral.primary"],
          textDisabled: theme["color.text.neutral.disabled"],
        },
      },
      radioGroup: {
        primary: {
          background: theme["color.background.neutral.strong.default"],
          backgroundHover: theme["color.background.neutral.strong.hovered"],
          backgroundSelected: theme["color.background.neutral.strong.pressed"],
          border: theme["color.border.neutral.strongest.default"],
          borderError: theme["color.border.support.danger.strongest.default"],
          borderHover: theme["color.border.neutral.strongest.hovered"],
          text: theme["color.text.neutral.primary"],
          textDisabled: theme["color.text.neutral.disabled"],
        },
      },
      button: {
        primary: {
          color: theme["color.text.neutral.primary"],
          background: theme["color.background.attention.strongest.default"],
          backgroundHover:
            theme["color.background.attention.strongest.hovered"],
        },
        secondary: {
          color: theme["color.text.neutral.secondary"],
          background:
            theme["color.background.attention.subtle.selected.default"],
          backgroundHover:
            theme["color.background.attention.subtle.selected.hovered"],
        },
      },
      frame: {
        background: "#0b58d0ff",
      },
      charts: {
        azure: theme["color.graphical.azure"],
        cyan: theme["color.graphical.cyan"],
        green: theme["color.graphical.green"],
        purple: theme["color.graphical.purple"],
        rose: theme["color.graphical.rose"],
        sand: theme["color.graphical.sand"],
        yellow: theme["color.graphical.yellow"],
        teal: "#2c7a7bff",
        lavender: "#b08ad6ff",
        crimson: "#a83a54ff",
        forest: "#216a3cff",
        gold: "#d4af37ff",
        slate: "#6a7b8cff",
        peach: "#f7a072ff",
        ocean: "#2a52beff",
        ruby: "#cc0066ff",
        lime: "#76c253ff",
      },
    },
    fonts: {
      xs: {
        fontSize: "0.625rem" /* 10px */,
        lineHeight: "0.75rem" /* 16px */,
      },
      sm: {
        fontSize: "0.75rem" /* 12px */,
        lineHeight: "1rem" /* 16px */,
      },
      base: {
        fontSize: "0.875rem" /* 14px */,
        lineHeight: "1.25rem" /* 20px */,
      },
      medium: {
        fontSize: "1rem" /* 16px */,
        lineHeight: "1.5rem" /* 24px */,
      },
      lg: {
        fontSize: "1.125rem" /* 18px */,
        lineHeight: "1.75rem" /* 28px */,
      },
      xl: {
        fontSize: "1.25rem" /* 20px */,
        lineHeight: "1.75rem" /* 28px */,
      },
      "2xl": {
        fontSize: "1.5rem" /* 24px */,
        lineHeight: "2rem" /* 32px */,
      },
      "3xl": {
        fontSize: "1.875rem" /* 30px */,
        lineHeight: "2.25rem" /* 36px */,
      },
      "4xl": {
        fontSize: "2.25rem" /* 36px */,
        lineHeight: "2.5rem" /* 40px */,
      },
      "5xl": {
        fontSize: "3rem" /* 48px */,
        lineHeight: "1",
      },
      "6xl": {
        fontSize: "3.75rem" /* 60px */,
        lineHeight: "1",
      },
      "7xl": {
        fontSize: "4.5rem" /* 72px */,
        lineHeight: "1",
      },
      "8xl": {
        fontSize: "6rem" /* 96px */,
        lineHeight: "1",
      },
      "9xl": {
        fontSize: "8rem" /* 128px */,
        lineHeight: "1",
      },
    },
    spacing: {
      0: "0",
      1: "4px",
      2: "8px",
      3: "12px",
      4: "16px",
      5: "24px",
      6: "32px",
      7: "48px",
    },
  } as GlobalTheme;
}
